import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons';
import { Popover } from '../components/ui';
import { BrandGlyph, BrandWordmark } from '../components/ui/brand';
import { MENU_ITEM, MenuItems, useMenuButton } from '../components/ui/menuButton';
import { authApi, inboxApi, workspaceApi } from '../lib/api';
import { useCurrentWorkspace } from '../features/billing/plan';
import { forgetPendingFlows } from '../features/posts/pendingFlows';
import { useSession } from './session';

/**
 * The pieces of chrome the desktop sidebar and the phone top bar share: the
 * workspace switcher, the account menu, and the unread count.
 */

/** Top of the plum gradient: the reply dot's knockout stroke is painted in it (charte §02). */
export const SIDEBAR_SURFACE = '#221a4b';

/**
 * Open conversations with unread messages. No dedicated endpoint: the list's
 * total. Not asked while the workspace is suspended: the server refuses it
 * (403) and the chrome would poll that refusal every 30 s (D-183).
 */
export function useUnreadCount(): number {
  const suspended = Boolean(useCurrentWorkspace().data?.suspendedAt);
  const unread = useQuery({
    queryKey: ['conversations', 'unread-count'],
    queryFn: () => inboxApi.list({ status: 'open', unread: true, limit: 1 }),
    refetchInterval: 30_000,
    enabled: !suspended,
  });
  return unread.data?.meta.total ?? 0;
}

export function initialsOf(firstName?: string | null, email?: string | null): string {
  if (firstName && firstName.length > 0) return firstName.slice(0, 1).toUpperCase();
  return (email ?? '?').slice(0, 1).toUpperCase();
}

/**
 * Logo, product name and the active workspace; opens the list of the user's
 * workspaces. Switching drops every cached query but the session, so nothing
 * of the previous workspace stays on screen, then reloads the session (its
 * active workspace changed) and lands on the new workspace's dashboard.
 */
export function WorkspaceSwitcher({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const menu = useMenuButton();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, refresh } = useSession();
  const current = useQuery({ queryKey: ['workspace', 'current'], queryFn: workspaceApi.current });
  const workspaces = useQuery({ queryKey: ['workspaces'], queryFn: workspaceApi.list, enabled: menu.open });

  const activate = useMutation({
    mutationFn: (id: string) => workspaceApi.activate(id),
    onSuccess: async () => {
      menu.close();
      navigate('/');
      await queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
      await refresh();
    },
  });

  const listed = workspaces.data ?? (current.data ? [current.data] : []);

  return (
    <>
      <button
        ref={menu.triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={t('shell.workspaceButton', { name: current.data?.name ?? t('shell.workspaceLoading') })}
        onClick={menu.toggle}
        className={`flex min-w-0 items-center gap-2.5 rounded-[10px] text-left transition-colors hover:bg-white/6 ${
          compact ? 'min-h-11 flex-1 px-1' : 'w-full px-2 py-1.5'
        }`}
      >
        <BrandGlyph size={compact ? 26 : 28} variant="on-dark" surface={SIDEBAR_SURFACE} className="shrink-0" />
        <span className="flex min-w-0 flex-1 flex-col">
          <BrandWordmark className="text-[15px] font-bold leading-tight text-white" />
          <span className="truncate text-[11.5px] leading-tight text-(--color-sidebar-muted)">{current.data?.name ?? '…'}</span>
        </span>
        <Icon name="chevronsUpDown" className="size-[15px] shrink-0 text-(--color-sidebar-label)" />
      </button>
      <Popover anchorRef={menu.triggerRef} open={menu.open} onClose={menu.close} width={240} role="menu" aria-label={t('shell.workspaces')}>
        <MenuItems onClose={menu.close}>
          <p className="px-2.5 pb-1 pt-1.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-(--color-ink-500)">{t('shell.workspaces')}</p>
          {listed.map((ws) => {
            const active = ws.id === session?.activeWorkspaceId;
            return (
              <button
                key={ws.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                disabled={activate.isPending}
                onClick={() => (active ? menu.close() : activate.mutate(ws.id))}
                className={MENU_ITEM}
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-(--color-accent-wash) text-xs font-bold text-(--color-accent-strong)">
                  {ws.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-(--color-ink-900)">{ws.name}</span>
                  <span className="block text-[11.5px] text-(--color-ink-500)">{t(`shell.role.${ws.role}`)}</span>
                </span>
                {active ? <Icon name="check" className="size-4 shrink-0 text-(--color-accent)" /> : null}
              </button>
            );
          })}
          {activate.isError ? (
            <p role="alert" className="px-2.5 py-1.5 text-[12px] text-(--color-danger-text)">
              {t('shell.switchFailed')}
            </p>
          ) : null}
          <div className="my-1 border-t border-(--color-line-faint)" />
          <Link to="/onboarding/workspace" role="menuitem" onClick={menu.close} className={MENU_ITEM}>
            <Icon name="plus" className="size-4 text-(--color-ink-500)" />
            {t('shell.createWorkspace')}
          </Link>
        </MenuItems>
      </Popover>
    </>
  );
}

function useLogout() {
  const { session, refresh } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: async () => {
      queryClient.clear();
      if (session) forgetPendingFlows(session.user.id);
      await refresh();
      navigate('/login', { replace: true });
    },
  });
}

/**
 * The signed-in person's menu: Settings, then Sign out apart in the danger tone
 * (R42). No name and email header: the sidebar row right under it already
 * shows them (A01-07). Opened by the ··· button on the sidebar's user row, or
 * by the avatar in the phone top bar.
 */
export function AccountMenu({ variant }: { variant: 'sidebar' | 'avatar' }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const menu = useMenuButton();
  const logout = useLogout();
  const initial = initialsOf(session?.user.firstName, session?.user.email);
  const triggerProps = {
    ref: menu.triggerRef,
    type: 'button' as const,
    'aria-label': t('shell.accountMenu'),
    'aria-haspopup': 'menu' as const,
    'aria-expanded': menu.open,
    onClick: menu.toggle,
  };

  return (
    <>
      {variant === 'sidebar' ? (
        <button
          {...triggerProps}
          title={t('shell.accountMenu')}
          className="touch-target flex size-7 shrink-0 items-center justify-center rounded-lg text-(--color-sidebar-muted) transition-colors hover:bg-white/8 hover:text-white"
        >
          <Icon name="more" className="size-4" />
        </button>
      ) : (
        <button {...triggerProps} className="flex size-11 shrink-0 items-center justify-center rounded-full">
          <span className="flex size-[34px] items-center justify-center rounded-full bg-[image:var(--gradient-avatar)] text-[13px] font-bold text-white">
            {initial}
          </span>
        </button>
      )}
      <Popover anchorRef={menu.triggerRef} open={menu.open} onClose={menu.close} width={220} align="end" role="menu" aria-label={t('shell.account')}>
        <MenuItems onClose={menu.close}>
          <Link to="/settings/people" role="menuitem" onClick={menu.close} className={MENU_ITEM}>
            <Icon name="settings" className="size-4 text-(--color-ink-500)" />
            {t('nav.settings')}
          </Link>
          <div className="my-1 border-t border-(--color-line-faint)" />
          <button
            type="button"
            role="menuitem"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className={`${MENU_ITEM} text-(--color-danger-text)! hover:bg-(--color-danger-wash)! hover:text-(--color-danger-text)!`}
          >
            <Icon name="logout" className="size-4" />
            {logout.isPending ? t('shell.signingOut') : t('shell.signOut')}
          </button>
          {logout.isError ? (
            <p role="alert" className="px-2.5 py-1.5 text-[12px] text-(--color-danger-text)">
              {t('shell.signOutFailed')}
            </p>
          ) : null}
        </MenuItems>
      </Popover>
    </>
  );
}
